import React, { FC, useId, useRef, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { SCUIButton } from '../../atoms/SCUIButton/SCUIButton';
import SCUIDownloadButton from '../../atoms/SCUIDownloadButton/SCUIDownloadButton';
import DeleteIcon from '@mui/icons-material/Delete';

interface ExampleData {
    [key: string]: any;
}

interface SCUIGenericExcelUploadProps {
    onUpload: (file: File) => void;
    accept?: string;
    title?: string;
    description?: string;
    uploadButtonText?: string;
    selectFileButtonText?: string;
    selectedFileText?: string;
    showExample?: boolean;
    exampleData?: ExampleData[];
    exampleFileName?: string;
    exampleButtonText?: string;
    disabled?: boolean;
    loading?: boolean;
    className?: string;
    style?: React.CSSProperties;
    /** Optional stable classes for E2E (e.g. page-specific selectors). */
    exampleWrapClassName?: string;
    dropzoneClassName?: string;
    fileInputClassName?: string;
    selectFileButtonClassName?: string;
    uploadButtonClassName?: string;
}

/** Dosya diyaloğu kapandıktan sonra alttaki Yükle butonuna düşen ghost-click'i engeller */
const FILE_DIALOG_CLICK_GUARD_MS = 500;

export const SCUIGenericExcelUpload: FC<SCUIGenericExcelUploadProps> = ({
    onUpload,
    accept = '.xlsx,.xls',
    title,
    description,
    uploadButtonText,
    selectFileButtonText,
    selectedFileText,
    showExample = false,
    exampleData = [],
    exampleFileName,
    exampleButtonText,
    disabled = false,
    loading = false,
    className = '',
    style,
    exampleWrapClassName,
    dropzoneClassName,
    fileInputClassName,
    selectFileButtonClassName,
    uploadButtonClassName,
}) => {
    const { t } = useTranslation();
    const inputId = useId();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const ignoreUploadUntilRef = useRef(0);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            // Dialog kapanınca aynı tıklama Yükle'ye düşebiliyor
            ignoreUploadUntilRef.current = Date.now() + FILE_DIALOG_CLICK_GUARD_MS;
            setSelectedFile(event.target.files[0]);
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleUpload = () => {
        if (!selectedFile) return;
        if (Date.now() < ignoreUploadUntilRef.current) return;
        onUpload(selectedFile);
    };

    return (
        <Box sx={{ p: 3, ...style }} className={className}>
            {showExample && exampleData.length > 0 && (
                <Box className={exampleWrapClassName}>
                    <SCUIDownloadButton
                        position="right"
                        style={{ marginBottom: 10 }}
                        excelData={{
                            excelDownloadData: exampleData,
                            excelDownloadButtonText: exampleButtonText || t('general.downloadExample'),
                            excelDownloadFolderName: exampleFileName || t('general.downloadExample'),
                        }}
                    />
                </Box>
            )}
            <Box
                className={dropzoneClassName}
                sx={{
                    border: '2px dashed #e0e0e0',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    bgcolor: '#fafafa',
                    mb: 3,
                }}
            >
                {title && (
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {title}
                    </Typography>
                )}
                <input
                    type="file"
                    accept={accept}
                    style={{ display: 'none' }}
                    id={inputId}
                    className={fileInputClassName}
                    onChange={handleFileSelect}
                    ref={inputRef}
                />
                <label htmlFor={inputId}>
                    <Button
                        component="span"
                        className={selectFileButtonClassName}
                        startIcon={<CloudUploadIcon />}
                        sx={{
                            color: '#00B8F4',
                            '&:hover': {
                                bgcolor: 'rgba(0, 184, 244, 0.04)',
                            },
                        }}
                    >
                        {selectFileButtonText || t('fastDeliveryCargoManagement.selectExcelFile')}
                    </Button>
                </label>
                {selectedFile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', mr: 1 }}>
                            {selectedFileText || selectedFile.name}
                        </Typography>
                        <DeleteIcon style={{ color: 'red', cursor: 'pointer' }} onClick={handleRemoveFile} />
                    </Box>
                )}
            </Box>

            {description && (
                <Box
                    sx={{
                        bgcolor: '#f5f5f5',
                        p: 2,
                        borderRadius: 1,
                        mb: 3,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <SCUIButton
                    text={uploadButtonText || t('fastDeliveryCargoManagement.upload')}
                    type="primary"
                    buttonType="button"
                    sizeType="default"
                    size="m"
                    onClick={handleUpload}
                    disabled={disabled || !selectedFile}
                    style={loading ? { opacity: 0.7 } : undefined}
                    className={uploadButtonClassName}
                />
            </Box>
        </Box>
    );
};

export default SCUIGenericExcelUpload;
